import FailedSalesPanel from "./FailedSalesPanel";
import WaitlistForm from "./WaitlistForm";
import MySalesReport from "./MySalesReport";
import Settings from "./Settings";
import Profile from "./Profile";
import AlertsBadge from "./AlertsBadge";
import PaymentPopup from "./PaymentPopup";
import BarcodeScanner from "./BarcodeScanner";
import Analytics from "./Analytics";
import Warehouse from "./Warehouse";
import Inventory from "./Inventory";
import Products from "./Products";
import Report from "./Report";
import Login from "./Login";
import Landing from "./Landing";
import Signup from "./Signup";
import Onboarding from "./Onboarding";
import TrialExpired from "./TrialExpired";
import PrivacyPolicy from "./PrivacyPolicy";
import Terms from "./Terms";
import React, { useState, useEffect, useRef } from "react";
import { supabase } from "./supabase";
import { logSupabaseError } from "./utils";
import "./App.css";
import lockupHorizontal from "./assets/brand/lockup-horizontal.svg";
import { useOffline } from "./hooks/useOffline";
import { useIsMobile } from "./hooks/useIsMobile";
import MobilePosBlocked from "./MobilePosBlocked";
import {
  cacheProducts,
  getCachedProducts,
  cacheCategories,
  getCachedCategories,
  queueSale,
  getPendingSales,
  getPendingCount,
  getAllPendingSales,
  updateSaleStatus,
  decrementCachedStock,
  restoreCachedStock,
  claimSaleForSync,
  recoverStuckSyncingSales,
} from "./db";

// The two public legal pages have real URLs so they load on a cold visit
// (Google's OAuth consent-screen verification requires that). Not a router:
// just these two paths, kept in sync with the address bar via the History
// API. Deliberately separate from `view` — see legalPage state in App.
const LEGAL_PAGE_PATHS = { privacy: "/privacy-policy", terms: "/terms" };

function legalPageForPath(pathname) {
  const path = pathname.replace(/\/+$/, "");
  return (
    Object.keys(LEGAL_PAGE_PATHS).find((k) => LEGAL_PAGE_PATHS[k] === path) ??
    null
  );
}

export default function App() {
  const { isOffline, justReconnected } = useOffline();
  const isMobile = useIsMobile();
  const [mobileAccessRequest, setMobileAccessRequest] = useState(null);
  const [pendingSyncCount, setPendingSyncCount] = useState(0);
  const [myFailedCount, setMyFailedCount] = useState(0);
  const [showMyFailedSales, setShowMyFailedSales] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null); // { synced, failed }
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showMySales, setShowMySales] = useState(false);
  const [view, setView] = useState("landing");
  // Which legal page ("privacy" | "terms" | null) is open, driven by the URL.
  // Kept out of `view` on purpose: the auth listener calls setView("landing")
  // on load even with no session, which would clobber a cold visit, and the
  // page renders as an overlay so an in-progress WaitlistForm underneath
  // isn't unmounted.
  const [legalPage, setLegalPage] = useState(() =>
    legalPageForPath(window.location.pathname),
  );
  const [store, setStore] = useState(null);
  const [trialDaysLeft, setTrialDaysLeft] = useState(null);
  const [showMobileNav, setShowMobileNav] = useState(false);
  const [showMobileCart, setShowMobileCart] = useState(false);
  const [storeSettings, setStoreSettings] = useState({
    vat_enabled: "false",
    vat_rate: "15",
    theme: "dark",
    store_name: "",
    store_tin: "",
    store_address: "",
    store_phone: "",
    receipt_print_enabled: "true",
    my_sales_enabled: "true",
    camera_scanner_enabled: "true",
    alerts_enabled: "true",
    discount_enabled: "false",
    mobile_pos_enabled: "true",
  });

  // Modal visibility
  const [showReport, setShowReport] = useState(false);
  const [showProducts, setShowProducts] = useState(false);
  const [showInventory, setShowInventory] = useState(false);
  const [showWarehouse, setShowWarehouse] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showCamScanner, setShowCamScanner] = useState(false);
  const [showPaymentPopup, setShowPaymentPopup] = useState(false);
  const [showFailedSales, setShowFailedSales] = useState(false);
  const [showWaitlistForm, setShowWaitlistForm] = useState(false);
  const [scanFeedback, setScanFeedback] = useState(null);

  // Barcode scanner refs
  const lastKeyTime = useRef(0);
  const barcodeBuffer = useRef("");
  const barcodeTimer = useRef(null);
  const roleCheckRef = useRef(null);
  const dividerRef = useRef(null);
  const isDragging = useRef(false);
  const [leftWidth, setLeftWidth] = useState(65); // percentage

  // ── Data fetching ─────────────────────────────────────────
  async function fetchProducts() {
    if (!navigator.onLine) {
      const cached = await getCachedProducts();
      if (cached.length > 0) {
        setProducts(cached);
        setLoading(false);
        return;
      }
      setLoading(false);
      return;
    }
    const { data, error } = await supabase
      .from("products")
      .select("*, categories(name, color, icon)")
      .eq("is_active", true)
      .order("name");
    logSupabaseError("App: fetchProducts", error);
    if (data) {
      setProducts(data);
      await cacheProducts(data);
    }
    setLoading(false);
  }

  async function fetchCategories() {
    if (!navigator.onLine) {
      const cached = await getCachedCategories();
      if (cached.length > 0) {
        setCategories(cached);
      }
      return;
    }
    const { data, error } = await supabase
      .from("categories")
      .select("*")
      .order("name");
    logSupabaseError("App: fetchCategories", error);
    if (data) {
      setCategories(data);
      await cacheCategories(data);
    }
  }

  async function fetchSettings() {
    const { data, error } = await supabase.from("store_settings").select("key, value");
    logSupabaseError("App: fetchSettings", error);
    if (data) {
      const map = {};
      data.forEach((s) => {
        map[s.key] = s.value;
      });
      setStoreSettings((prev) => ({ ...prev, ...map }));
      applyTheme(map.theme ?? "dark");
    }
  }

  async function fetchMobileAccessRequest(cashierId) {
    const { data, error } = await supabase
      .from("mobile_access_requests")
      .select("*")
      .eq("cashier_id", cashierId)
      .order("requested_at", { ascending: false })
      .limit(1)
      .maybeSingle();
    logSupabaseError("App: fetchMobileAccessRequest", error);
    setMobileAccessRequest(data ?? null);
  }

  async function handleRequestMobileAccess() {
    if (!profile || !store) return;
    const { data, error } = await supabase
      .from("mobile_access_requests")
      .insert({ store_id: store.id, cashier_id: profile.id })
      .select()
      .single();
    if (error) {
      logSupabaseError("App: handleRequestMobileAccess", error);
      alert("❌ Failed to submit request: " + error.message);
      return;
    }
    setMobileAccessRequest(data);
  }

  function applyTheme(theme) {
    const root = document.documentElement;
    if (theme === "light") {
      root.style.setProperty("--bg", "#F5F5F7");
      root.style.setProperty("--surface", "#FFFFFF");
      root.style.setProperty("--elevated", "#F0F0F2");
      root.style.setProperty("--card", "#E8E8EA");
      root.style.setProperty("--border", "#D1D1D6");
      root.style.setProperty("--border2", "#C7C7CC");
      root.style.setProperty("--text", "#1C1C1E");
      root.style.setProperty("--text2", "#3A3A3C");
      root.style.setProperty("--text3", "#6C6C70");
    } else {
      root.style.setProperty("--bg", "#0E0E10");
      root.style.setProperty("--surface", "#161618");
      root.style.setProperty("--elevated", "#1C1C1E");
      root.style.setProperty("--card", "#242426");
      root.style.setProperty("--border", "#2C2C2E");
      root.style.setProperty("--border2", "#3A3A3C");
      root.style.setProperty("--text", "#F5F5F7");
      root.style.setProperty("--text2", "#98989F");
      root.style.setProperty("--text3", "#5A5A60");
    }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    // A row can only be stuck "syncing" if the app closed/crashed mid-call
    // on a prior run (a completed sync always resolves it to "synced" or
    // "failed") — reset it to "pending" so it's picked up again. This is a
    // deliberate "assume failed, allow retry" choice: if the original
    // process_sale call actually succeeded server-side and only the
    // in-app confirmation was lost, this retry will double-charge that
    // sale. See syncPendingSales() for the claim mechanism this recovers.
    recoverStuckSyncingSales();
    fetchProducts();
    fetchCategories();
  }, []);

  // Sync when reconnected
  useEffect(() => {
    if (justReconnected) {
      syncPendingSales();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [justReconnected]);

  // Keep pending count updated — and proactively attempt a sync whenever
  // there's a real opportunity to, not only on the justReconnected
  // transition, so sales spend less time sitting unsynced (and therefore
  // less time exposed to the shift-change identity-mismatch risk).
  // syncPendingSales() already no-ops via its own isSyncing check, and any
  // row it does attempt is claimed atomically first (claimSaleForSync), so
  // calling it opportunistically here is safe even alongside the
  // justReconnected-triggered sync. Depends on user?.id (not []) so this
  // doesn't run against a "logged out" value captured before login
  // resolved on mount.
  useEffect(() => {
    async function updateCount() {
      const count = await getPendingCount();
      setPendingSyncCount(count);
      if (count > 0 && navigator.onLine && user) {
        syncPendingSales();
      }

      if (user) {
        const all = await getAllPendingSales();
        setMyFailedCount(
          all.filter((s) => s.status === "failed" && s.p_cashier_id === user.id)
            .length,
        );
      } else {
        setMyFailedCount(0);
      }
    }
    updateCount();
    const interval = setInterval(updateCount, 5000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // ── Legal pages (URL-driven) ──────────────────────────────
  // Back/forward re-derives the open legal page from the address bar.
  useEffect(() => {
    function onPopState() {
      setLegalPage(legalPageForPath(window.location.pathname));
    }
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  function openLegalPage(page) {
    window.history.pushState({ legalPage: page }, "", LEGAL_PAGE_PATHS[page]);
    setLegalPage(page);
  }

  function closeLegalPage() {
    // Opened in-app: step back over the entry openLegalPage pushed, so
    // browser Back/Forward stay consistent. Cold visit: there's no in-app
    // entry behind this one (Back would leave the site), so push "/".
    if (window.history.state?.legalPage) {
      window.history.back();
    } else {
      window.history.pushState(null, "", "/");
      setLegalPage(null);
    }
  }

  // ── Auth listener ─────────────────────────────────────────
  useEffect(() => {
    // Private invite link (?invite=1): the real Signup view is otherwise
    // unreachable from any UI now (see WaitlistForm session) — this is the
    // one static, narrow URL check that lets it be reached directly, e.g.
    // https://suqpos.com/?invite=1. Not a router: computed once here and
    // only ever consulted in the "no session" branch below, so it can
    // only ever route a logged-OUT visitor to signup — an already
    // authenticated visitor who opens the same link never reaches this
    // branch at all (their session routes them to "loading" instead,
    // same as always), so normal session-based routing is untouched for
    // them, per spec.
    const hasInviteParam = new URLSearchParams(window.location.search).has(
      "invite",
    );

    // Check existing session on mount — let fetchProfile handle routing
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) {
        setUser(session.user);
        setView("loading");
      }
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user) {
        console.log("onAuthStateChange: SIGNED_IN", session.user.id);
        setUser(session.user);
        setView("loading");
      } else {
        setUser(null);
        setView(hasInviteParam ? "signup" : "landing");
        setStore(null);
        setProfile(null);
        setMobileAccessRequest(null);
        setStoreSettings({
          vat_enabled: "false",
          vat_rate: "15",
          theme: "dark",
          store_name: "",
          store_tin: "",
          store_address: "",
          store_phone: "",
          receipt_print_enabled: "true",
          my_sales_enabled: "true",
          camera_scanner_enabled: "true",
          alerts_enabled: "true",
          discount_enabled: "false",
          mobile_pos_enabled: "true",
        });
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  // ── Profile + store + trial fetch ────────────────────────
  useEffect(() => {
    async function fetchProfile() {
      if (!user) {
        console.log("fetchProfile: no user, returning");
        if (roleCheckRef.current) {
          clearInterval(roleCheckRef.current);
          roleCheckRef.current = null;
        }
        return;
      }

      console.log("fetchProfile: running for user", user.id);

      const { data, error: profileError } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", user.id)
        .single();

      logSupabaseError("App: fetchProfile", profileError);

      if (!data) {
        console.log("fetchProfile: no profile found, going to onboarding");
        setView("onboarding");
        return;
      }

      setProfile(data);

      if (data.store_id) {
        const { data: storeData, error: storeError } = await supabase
          .from("stores")
          .select("*")
          .eq("id", data.store_id)
          .single();
        logSupabaseError("App: fetch store", storeError);

        if (storeData) {
          setStore(storeData);
          const daysLeft = Math.ceil(
            (new Date(storeData.trial_ends_at) - new Date()) /
              (1000 * 60 * 60 * 24),
          );
          setTrialDaysLeft(daysLeft);

          const trialActive = daysLeft > 0;
          const subscriptionActive =
            storeData.subscription_status === "active" &&
            storeData.paid_until &&
            new Date(storeData.paid_until) > new Date();

          if (!trialActive && !subscriptionActive) {
            setView("trial-expired");
            return;
          }
        }
        // Load store data then go to app
        await fetchSettings();
        await fetchProducts();
        await fetchCategories();
        if (data.role === "cashier") {
          await fetchMobileAccessRequest(data.id);
        }
        setView("app");
      } else {
        // No store yet — go to onboarding
        setView("onboarding");
      }

      startRoleWatch(data.role);
    }

    function startRoleWatch(currentRole) {
      if (roleCheckRef.current) clearInterval(roleCheckRef.current);
      roleCheckRef.current = setInterval(async () => {
        const { data, error } = await supabase
          .from("profiles")
          .select("role")
          .eq("id", user.id)
          .single();
        logSupabaseError("App: role-check poll", error);
        if (!data) return;
        if (data.role !== currentRole) {
          clearInterval(roleCheckRef.current);
          roleCheckRef.current = null;
          await supabase.auth.signOut();
        }
      }, 60 * 1000);
    }

    fetchProfile();

    return () => {
      if (roleCheckRef.current) {
        clearInterval(roleCheckRef.current);
        roleCheckRef.current = null;
      }
    };
  }, [user]);

  // ── Filtering ─────────────────────────────────────────────
  const filtered = products.filter((p) => {
    const matchesSearch =
      p.name.toLowerCase().includes(search.toLowerCase()) ||
      (p.barcode ?? "").includes(search) ||
      (p.sku_code ?? "").toLowerCase().includes(search.toLowerCase());
    const matchesCategory =
      !selectedCategory || p.category_id === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // ── Cart totals ───────────────────────────────────────────
  const cartSubtotal = cart.reduce(
    (sum, i) => sum + Number(i.selling_price ?? 0) * Number(i.qty ?? 0),
    0,
  );
  const vatEnabled = storeSettings?.vat_enabled === "true";
  const vatRate = Number(storeSettings?.vat_rate ?? 0) / 100;
  const vatAmount = vatEnabled ? cartSubtotal * vatRate : 0;
  const totalWithVat = cartSubtotal + vatAmount;

  // ── Cart actions ──────────────────────────────────────────
  function addToCart(product) {
    if (product.stock_qty <= 0) return;
    setCart((prev) => {
      const exists = prev.find((i) => i.id === product.id);
      if (exists) {
        if (exists.qty >= product.stock_qty) return prev;
        return prev.map((i) =>
          i.id === product.id ? { ...i, qty: i.qty + 1 } : i,
        );
      }
      return [...prev, { ...product, qty: 1 }];
    });
  }

  function removeFromCart(id) {
    setCart((prev) => prev.filter((i) => i.id !== id));
  }

  // ── Barcode scanner ───────────────────────────────────────
  function handleBarcodeScanned(barcode) {
    if (!barcode) return;
    const product = products.find(
      (p) => p.barcode === barcode || p.sku_code === barcode,
    );
    if (!product) {
      setSearch(barcode);
      setScanFeedback({
        type: "error",
        message: `❌ No product found for: ${barcode}`,
      });
      setTimeout(() => setScanFeedback(null), 2500);
      return;
    }
    if (product.stock_qty <= 0) {
      setScanFeedback({
        type: "error",
        message: `❌ ${product.name} is out of stock!`,
      });
      setTimeout(() => setScanFeedback(null), 2500);
      return;
    }
    addToCart(product);
    setScanFeedback({
      type: "success",
      message: `✅ ${product.name} added to cart!`,
    });
    setTimeout(() => setScanFeedback(null), 1500);
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    function handleKeyDown(e) {
      if (!e || !e.key) return;
      const tag = document.activeElement?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return;
      // Ignore non-printable keys except Enter
      if (e.key.length > 1 && e.key !== "Enter") return;
      const now = Date.now();
      const diff = now - lastKeyTime.current;
      lastKeyTime.current = now;
      barcodeBuffer.current = diff < 50 ? barcodeBuffer.current + e.key : e.key;
      clearTimeout(barcodeTimer.current);
      if (e.key === "Enter" && barcodeBuffer.current.length > 3) {
        handleBarcodeScanned(barcodeBuffer.current.replace("Enter", "").trim());
        barcodeBuffer.current = "";
        return;
      }
      barcodeTimer.current = setTimeout(() => {
        if (barcodeBuffer.current.length > 3) {
          handleBarcodeScanned(barcodeBuffer.current.trim());
          barcodeBuffer.current = "";
        }
      }, 100);
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      clearTimeout(barcodeTimer.current);
    };
  }, [products]);

  // ADD this effect after the other useEffects:
  useEffect(() => {
    function onMouseMove(e) {
      if (!isDragging.current) return;
      const container = dividerRef.current?.parentElement;
      if (!container) return;
      const rect = container.getBoundingClientRect();
      const pct = ((e.clientX - rect.left) / rect.width) * 100;
      setLeftWidth(Math.min(Math.max(pct, 35), 80)); // clamp 35%–80%
    }
    function onMouseUp() {
      isDragging.current = false;
      document.body.style.cursor = "";
    }
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, []);

  // ── Checkout ──────────────────────────────────────────────
  async function handleCheckout(amountTendered) {
    if (cart.length === 0) return;

    const subtotal = cart.reduce((sum, i) => sum + i.selling_price * i.qty, 0);
    const isVat = storeSettings.vat_enabled === "true";
    const vRate = Number(storeSettings.vat_rate ?? 0) / 100;
    const vAmount = isVat ? subtotal * vRate : 0;
    const total = subtotal + vAmount;
    const items = cart.map((item) => ({
      product_id: item.id,
      product_name: item.name,
      quantity: item.qty,
      unit_price: item.selling_price,
      line_total: item.selling_price * item.qty,
    }));

    // ── OFFLINE PATH ──────────────────────────────────────
    if (!navigator.onLine) {
      for (const item of cart) {
        const ok = await decrementCachedStock(item.id, item.qty);
        if (!ok) {
          for (const prev of cart) {
            if (prev.id === item.id) break;
            await restoreCachedStock(prev.id, prev.qty);
          }
          alert(`❌ Insufficient stock for "${item.name}". Sale blocked.`);
          return;
        }
      }

      await queueSale({
        p_cashier_id: user.id,
        p_store_id: profile?.store_id,
        p_subtotal: subtotal,
        p_tax_rate: isVat ? vRate : 0,
        p_tax_amount: vAmount,
        p_total_amount: total,
        p_payment_method: paymentMethod,
        p_amount_tendered: amountTendered ?? total,
        p_change_amount:
          paymentMethod === "cash" && amountTendered
            ? Math.max(0, amountTendered - total)
            : 0,
        p_customer_name: null,
        p_notes: "offline_sale",
        p_items: items,
        cart_snapshot: cart,
      });

      const count = await getPendingCount();
      setPendingSyncCount(count);

      const cached = await getCachedProducts();
      if (cached.length > 0) setProducts(cached);

      setCart([]);
      setShowMobileCart(false);
      return;
    }

    // ── ONLINE PATH ───────────────────────────────────────
    try {
      const { error } = await supabase.rpc("process_sale", {
        p_cashier_id: user.id,
        p_subtotal: subtotal,
        p_tax_rate: isVat ? vRate : 0,
        p_tax_amount: vAmount,
        p_total_amount: total,
        p_payment_method: paymentMethod,
        p_amount_tendered: amountTendered ?? total,
        p_change_amount:
          paymentMethod === "cash" && amountTendered
            ? Math.max(0, amountTendered - total)
            : 0,
        p_customer_name: null,
        p_notes: null,
        p_items: items,
      });
      if (error) throw error;
      setCart([]);
      setShowMobileCart(false);
      fetchProducts();
    } catch (error) {
      alert("❌ Checkout error: " + error.message);
    }
  }

  async function syncPendingSales() {
    if (isSyncing) return;
    const pending = await getPendingSales();
    if (pending.length === 0) return;

    setIsSyncing(true);
    let synced = 0;
    let failed = 0;

    for (const sale of pending) {
      // Claim the row (pending -> syncing) before calling process_sale, so
      // a second sync run — another tab/window sharing this same
      // IndexedDB, or a rapid reconnect flap — can't also pick up this
      // same row while this call is in flight. If the claim fails, someone
      // else already has it; skip without touching its status.
      const claimed = await claimSaleForSync(sale.local_id);
      if (!claimed) continue;

      try {
        const { error } = await supabase.rpc("process_sale", {
          p_cashier_id: sale.p_cashier_id,
          p_subtotal: sale.p_subtotal,
          p_tax_rate: sale.p_tax_rate,
          p_tax_amount: sale.p_tax_amount,
          p_total_amount: sale.p_total_amount,
          p_payment_method: sale.p_payment_method,
          p_amount_tendered: sale.p_amount_tendered,
          p_change_amount: sale.p_change_amount,
          p_customer_name: sale.p_customer_name,
          p_notes: sale.p_notes,
          p_items: sale.p_items,
        });
        if (error) throw error;
        await updateSaleStatus(sale.local_id, "synced");
        synced++;
      } catch (err) {
        await updateSaleStatus(sale.local_id, "failed", err.message);
        failed++;
      }
    }

    setIsSyncing(false);
    setSyncResult({ synced, failed });
    setTimeout(() => setSyncResult(null), 6000);

    fetchProducts();
    fetchCategories();

    const remaining = await getPendingCount();
    setPendingSyncCount(remaining);
  }

  // ── Sign out ──────────────────────────────────────────────
  // Guards against the shift-change scenario: signing out while this
  // device is still holding this cashier's unsynced sales lets the next
  // person's session take over before they sync, which process_sale
  // rejects (p_cashier_id won't match the new auth.uid()). Try to clear
  // the queue first; only prompt if that doesn't fully succeed.
  async function handleSignOut() {
    let pending = await getPendingSales();

    if (pending.length > 0 && navigator.onLine) {
      await syncPendingSales();
      pending = await getPendingSales();
    }

    if (pending.length > 0) {
      const cashierLabel = profile?.full_name ?? user?.email ?? "This account";
      const proceed = window.confirm(
        `⚠️ ${pending.length} offline sale${pending.length !== 1 ? "s" : ""} on this device ${pending.length !== 1 ? "haven't" : "hasn't"} synced yet.\n\n` +
          `If someone else signs in before ${pending.length !== 1 ? "they" : "it"} sync${pending.length !== 1 ? "" : "s"}, ${pending.length !== 1 ? "they'll" : "it'll"} fail — ${cashierLabel} will need to sign back in to retry them.\n\n` +
          `Sign out anyway?`,
      );
      if (!proceed) return;
    }

    await supabase.auth.signOut();
    setUser(null);
    setProfile(null);
  }

  // ── View routing ──────────────────────────────────────────
  const legalPageEl =
    legalPage === "privacy" ? (
      <PrivacyPolicy onClose={closeLegalPage} />
    ) : legalPage === "terms" ? (
      <Terms onClose={closeLegalPage} />
    ) : null;
  const waitlistFormEl = showWaitlistForm && (
    <WaitlistForm
      onClose={() => setShowWaitlistForm(false)}
      onOpenPrivacy={() => openLegalPage("privacy")}
      onOpenTerms={() => openLegalPage("terms")}
    />
  );

  // landing/login render it as an overlay (.legal-page is fixed, z-index 300)
  // so the WaitlistForm they can host stays mounted underneath; any other
  // view has nothing to preserve, so it simply takes the screen.
  if (legalPageEl && view !== "landing" && view !== "login") return legalPageEl;

  if (view === "landing")
    return (
      <>
        <Landing
          onSignUp={() => setShowWaitlistForm(true)}
          onLogin={() => setView("login")}
          onThemeToggle={() => {
            const next = storeSettings.theme === "dark" ? "light" : "dark";
            setStoreSettings((prev) => ({ ...prev, theme: next }));
            applyTheme(next);
          }}
          currentTheme={storeSettings.theme}
          onPrivacyPolicy={() => openLegalPage("privacy")}
          onTerms={() => openLegalPage("terms")}
        />
        {waitlistFormEl}
        {legalPageEl}
      </>
    );
  if (view === "signup")
    return (
      <Signup
        onSuccess={(u) => {
          setUser(u);
          setView("onboarding");
        }}
        onLogin={() => setView("login")}
      />
    );
  if (view === "login")
    return (
      <>
        <Login
          onLogin={(u) => {
            setUser(u);
            setView("loading");
          }}
          onSignUp={() => setShowWaitlistForm(true)}
          onBack={() => setView("landing")}
        />
        {waitlistFormEl}
        {legalPageEl}
      </>
    );
  if (view === "loading")
    return (
      <div
        style={{
          minHeight: "100vh",
          background: "var(--bg)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          gap: "20px",
        }}
      >
        <svg
          className="loading-icon"
          width="210"
          height="187"
          viewBox="210 62 180 160"
          fill="none"
          role="img"
        >
          <title>SuqPOS</title>
          <path
            className="loading-arch-outer"
            stroke="#32D4E0"
            strokeWidth="7"
            strokeLinecap="round"
            d="M 240 210 L 240 140 Q 240 80 300 80 Q 360 80 360 140 L 360 210"
          />
          <path
            className="loading-arch-inner"
            stroke="#FFB340"
            strokeWidth="7"
            strokeLinecap="round"
            d="M 258 210 L 258 148 Q 258 108 300 108 Q 342 108 342 148 L 342 210"
          />
          <g className="loading-icon-details">
            <line
              x1="228"
              y1="210"
              x2="372"
              y2="210"
              stroke="#32D4E0"
              strokeWidth="5"
              strokeLinecap="round"
            />
            <circle cx="300" cy="80" r="7" fill="#32D4E0" />
            <circle cx="300" cy="108" r="5" fill="#FFB340" />
            <line
              x1="228"
              y1="175"
              x2="248"
              y2="175"
              stroke="#32D4E0"
              strokeWidth="3.5"
              strokeLinecap="round"
              opacity="0.4"
            />
            <line
              x1="352"
              y1="175"
              x2="372"
              y2="175"
              stroke="#32D4E0"
              strokeWidth="3.5"
              strokeLinecap="round"
              opacity="0.4"
            />
            <line
              x1="240"
              y1="165"
              x2="228"
              y2="158"
              stroke="#32D4E0"
              strokeWidth="3"
              strokeLinecap="round"
              opacity="0.5"
            />
            <line
              x1="360"
              y1="165"
              x2="372"
              y2="158"
              stroke="#32D4E0"
              strokeWidth="3"
              strokeLinecap="round"
              opacity="0.5"
            />
          </g>
        </svg>

        <div className="loading-wordmark">
          <span style={{ color: "#32D4E0" }}>Suq</span>
          <span style={{ color: "#FFB340", fontWeight: 600 }}>POS</span>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <p style={{ color: "var(--text3)", fontSize: "13px", margin: 0 }}>
            Loading your store
          </p>
          <span className="loading-dots">
            <span className="loading-dot" style={{ animationDelay: "0s" }} />
            <span
              className="loading-dot"
              style={{ animationDelay: "0.2s" }}
            />
            <span
              className="loading-dot"
              style={{ animationDelay: "0.4s" }}
            />
          </span>
        </div>
      </div>
    );
  if (view === "onboarding")
    return (
      <Onboarding
        user={user}
        onComplete={() => {
          setView("loading");
          setUser((u) => ({ ...u, _refresh: Date.now() }));
        }}
      />
    );
  if (view === "trial-expired")
    return (
      <TrialExpired
        store={store}
        onSignOut={() => {
          setUser(null);
          setView("landing");
        }}
      />
    );

  // Managers always get full mobile access — only cashiers are gated.
  if (
    isMobile &&
    profile?.role === "cashier" &&
    storeSettings.mobile_pos_enabled === "false" &&
    mobileAccessRequest?.status !== "approved"
  ) {
    if (showMySales)
      return (
        <MySalesReport
          user={user}
          storeSettings={storeSettings}
          onClose={() => setShowMySales(false)}
        />
      );
    return (
      <MobilePosBlocked
        storeName={storeSettings.store_name || store?.name || "SuqPOS"}
        mySalesEnabled={storeSettings.my_sales_enabled !== "false"}
        onOpenMySales={() => setShowMySales(true)}
        accessRequest={mobileAccessRequest}
        onRequestAccess={handleRequestMobileAccess}
      />
    );
  }

  // ── POS (view === "app") ──────────────────────────────────
  return (
    <div className={`app${isOffline || isSyncing ? " has-banner" : ""}`}>
      {/* Trial warning banner */}
      {trialDaysLeft !== null && trialDaysLeft > 0 && trialDaysLeft <= 3 && (
        <div className="trial-warning-banner">
          ⚠️ Your free trial expires in {trialDaysLeft} day
          {trialDaysLeft !== 1 ? "s" : ""}. Contact us to continue using SuqPOS.
        </div>
      )}

      {/* Scan feedback toast */}
      {scanFeedback && (
        <div className={`scan-feedback ${scanFeedback.type}`}>
          {scanFeedback.message}
        </div>
      )}

      {/* Offline banner */}
      {isOffline && (
        <div className="offline-banner">
          <div className="offline-banner-dot" />
          📵 Offline mode — sales are being saved locally
          {pendingSyncCount > 0 && (
            <span className="offline-pending-count">
              {pendingSyncCount} pending
            </span>
          )}
        </div>
      )}

      {/* Syncing banner */}
      {!isOffline && isSyncing && (
        <div className="sync-banner">
          <div className="sync-spinner" />
          Syncing {pendingSyncCount} offline sale
          {pendingSyncCount !== 1 ? "s" : ""}...
        </div>
      )}

      {/* Sync result toast */}
      {syncResult && (
        <div
          className="offline-sale-toast"
          onClick={() => {
            if (syncResult.failed > 0 && profile?.role === "manager") {
              setShowFailedSales(true);
            }
          }}
          style={
            syncResult.failed > 0 && profile?.role === "manager"
              ? { cursor: "pointer" }
              : undefined
          }
        >
          {syncResult.failed === 0
            ? `✅ ${syncResult.synced} offline sale${syncResult.synced !== 1 ? "s" : ""} synced successfully`
            : profile?.role === "manager"
              ? `⚠ ${syncResult.synced} synced · ${syncResult.failed} failed — click to open Failed Sales`
              : `⚠ ${syncResult.synced} synced · ${syncResult.failed} failed — notify your manager`}
        </div>
      )}

      {/* ── Top Bar ── */}
      <header className="topbar">
        <div className="topbar-brand">
          <img
            src={lockupHorizontal}
            alt="SuqPOS"
            className="topbar-logo"
            style={{ height: "32px", width: "auto" }}
          />
          <span className="topbar-name">
            {storeSettings.store_name || "SuqPOS"}
          </span>
        </div>

        <div className="topbar-right">
          {profile?.role === "manager" && (
            <>
              {/* Desktop nav */}
              <div className="manager-buttons">
                <button
                  className="mgr-btn"
                  onClick={() => setShowProducts(true)}
                >
                  📦 Products
                </button>
                <button
                  className="mgr-btn"
                  onClick={() => setShowWarehouse(true)}
                >
                  🏪 Warehouse
                </button>
                <button
                  className="mgr-btn"
                  onClick={() => setShowInventory(true)}
                >
                  📋 Inventory
                </button>
                <button className="mgr-btn" onClick={() => setShowReport(true)}>
                  📊 Reports
                </button>
                <button
                  className="mgr-btn"
                  onClick={() => setShowAnalytics(true)}
                >
                  📈 Analytics
                </button>
                <button
                  className="mgr-btn"
                  onClick={() => setShowSettings(true)}
                >
                  ⚙️ Settings
                </button>
                <button
                  className="mgr-btn"
                  onClick={() => setShowFailedSales(true)}
                >
                  ⚠️ Failed Sales
                </button>
              </div>
              {/* Mobile hamburger */}
              <button
                className="mobile-menu-btn"
                onClick={() => setShowMobileNav((v) => !v)}
                style={{ display: "none" }}
                id="mobile-menu-btn"
              >
                {showMobileNav ? "✕" : "☰"}
              </button>
              {showMobileNav && (
                <div className="mobile-nav-drawer">
                  {[
                    {
                      label: "📦 Products",
                      action: () => setShowProducts(true),
                    },
                    {
                      label: "🏪 Warehouse",
                      action: () => setShowWarehouse(true),
                    },
                    {
                      label: "📋 Inventory",
                      action: () => setShowInventory(true),
                    },
                    { label: "📊 Reports", action: () => setShowReport(true) },
                    {
                      label: "📈 Analytics",
                      action: () => setShowAnalytics(true),
                    },
                    {
                      label: "⚙️ Settings",
                      action: () => setShowSettings(true),
                    },
                    {
                      label: "⚠️ Failed Sales",
                      action: () => setShowFailedSales(true),
                    },
                  ].map((item) => (
                    <button
                      key={item.label}
                      className="mgr-btn"
                      onClick={() => {
                        item.action();
                        setShowMobileNav(false);
                      }}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}

          {profile?.role === "cashier" &&
            storeSettings.my_sales_enabled !== "false" && (
              <button
                onClick={() => setShowMySales(true)}
                className="my-sales-btn"
              >
                🧾 My Sales
              </button>
            )}

          {storeSettings.alerts_enabled !== "false" && <AlertsBadge />}

          {myFailedCount > 0 && (
            <button
              className="my-sales-btn"
              onClick={() => setShowMyFailedSales(true)}
              title="Sales you queued that failed to sync — you're the only one who can retry them"
              style={{ borderColor: "var(--red)", color: "var(--red)" }}
            >
              ⚠️ {myFailedCount} Failed
            </button>
          )}

          <div
            className="user-badge"
            onClick={() => setShowProfile(true)}
            style={{ cursor: "pointer" }}
            title="Click to edit profile"
          >
            <span className="user-name">
              {profile?.full_name ?? user?.email}
            </span>
            <span className={`user-role ${profile?.role}`}>
              {profile?.role ?? "..."}
            </span>
          </div>

          <button
            className={`signout-btn${profile?.role === "manager" ? " signout-btn-mgr" : ""}`}
            onClick={handleSignOut}
            title="Sign out"
          >
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
          </button>
        </div>
      </header>

      {/* ── Modals ── */}
      {showReport && (
        <Report
          onClose={() => setShowReport(false)}
          storeSettings={storeSettings}
        />
      )}
      {showAnalytics && <Analytics onClose={() => setShowAnalytics(false)} />}
      {showFailedSales && (
        <FailedSalesPanel onClose={() => setShowFailedSales(false)} />
      )}
      {showMyFailedSales && (
        <FailedSalesPanel
          scopeToUserId={user?.id}
          onClose={() => setShowMyFailedSales(false)}
        />
      )}
      {showProfile && (
        <Profile
          onClose={() => setShowProfile(false)}
          user={user}
          profile={profile}
          onProfileUpdate={(u) => setProfile(u)}
        />
      )}
      {showSettings && (
        <Settings
          onClose={() => {
            setShowSettings(false);
            fetchSettings();
          }}
          currentUser={user}
          onSignOut={handleSignOut}
        />
      )}
      {showCamScanner && (
        <BarcodeScanner
          onScan={handleBarcodeScanned}
          onClose={() => setShowCamScanner(false)}
        />
      )}
      {showMySales && (
        <MySalesReport
          user={user}
          storeSettings={storeSettings}
          onClose={() => setShowMySales(false)}
        />
      )}
      {showProducts && (
        <Products
          onClose={() => {
            setShowProducts(false);
            fetchProducts();
          }}
          onCategoriesChange={() => {
            fetchCategories();
            fetchProducts();
          }}
        />
      )}
      {showWarehouse && (
        <Warehouse
          onClose={() => {
            setShowWarehouse(false);
            fetchProducts();
          }}
          user={user}
        />
      )}
      {showInventory && (
        <Inventory
          onClose={() => {
            setShowInventory(false);
            fetchProducts();
          }}
          user={user}
        />
      )}
      {showPaymentPopup && (
        <PaymentPopup
          total={totalWithVat}
          isOffline={isOffline}
          subtotal={cartSubtotal}
          vatAmount={vatAmount}
          vatRate={vatRate}
          paymentMethod={paymentMethod}
          cartItems={cart}
          storeSettings={storeSettings}
          onConfirm={async (tendered) => {
            setShowPaymentPopup(false);
            await handleCheckout(tendered);
          }}
          onCancel={() => setShowPaymentPopup(false)}
          printEnabled={storeSettings.receipt_print_enabled !== "false"}
        />
      )}

      {/* ── Main POS Layout ── */}
      <div className="main" style={{ display: "flex", overflow: "hidden" }}>
        {/* LEFT — Products */}
        <div className="left" style={{ width: isMobile ? "100%" : `${leftWidth}%`, minWidth: 0 }}>
          {/* Search + Scanner */}
          <div className="search-box search-box-row">
            <div style={{ flex: 1, minWidth: 0 }}>
              <input
                type="text"
                placeholder="🔍 Search or scan barcode..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            {storeSettings.camera_scanner_enabled !== "false" && (
              <button
                className="scanner-trigger-btn"
                onClick={() => setShowCamScanner(true)}
                title="Open camera scanner"
              >
                📷
              </button>
            )}
          </div>

          {/* Category Filter */}
          {categories.length > 0 && (
            <div className="cat-filter-wrap">
              <div className="cat-filter-bar">
                <button
                  className={`cat-filter-btn ${!selectedCategory ? "active" : ""}`}
                  onClick={() => setSelectedCategory(null)}
                >
                  ALL
                </button>
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    className={`cat-filter-btn ${selectedCategory === cat.id ? "active" : ""}`}
                    style={
                      selectedCategory === cat.id
                        ? {
                            background: cat.color + "20",
                            borderColor: cat.color + "60",
                            color: cat.color,
                          }
                        : {}
                    }
                    onClick={() =>
                      setSelectedCategory(
                        selectedCategory === cat.id ? null : cat.id,
                      )
                    }
                  >
                    {cat.icon} {cat.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Product Grid */}
          <div className="product-grid">
            {loading && (
              <p
                style={{
                  color: "var(--text3)",
                  gridColumn: "1/-1",
                  textAlign: "center",
                  padding: "40px",
                }}
              >
                Loading products...
              </p>
            )}
            {!loading && filtered.length === 0 && (
              <p
                style={{
                  color: "var(--text3)",
                  gridColumn: "1/-1",
                  textAlign: "center",
                  padding: "40px",
                }}
              >
                No products found
              </p>
            )}
            {!loading &&
              filtered.map((product) => {
                const cat = categories.find(
                  (c) => c.id === product.category_id,
                );
                return (
                  <button
                    key={product.id}
                    className={`product-card ${product.stock_qty <= 0 ? "disabled" : ""}`}
                    onClick={() => product.stock_qty > 0 && addToCart(product)}
                  >
                    {cat && (
                      <div className="cat-tag" style={{ color: cat.color }}>
                        {cat.icon} {cat.name}
                      </div>
                    )}
                    <h3>{product.name}</h3>
                    {product.sku_code && (
                      <p
                        style={{
                          fontSize: "10px",
                          color: "var(--text3)",
                          fontFamily: "var(--mono)",
                          margin: "2px 0",
                        }}
                      >
                        {product.sku_code}
                      </p>
                    )}
                    {product.shelf_location && (
                      <p
                        style={{
                          fontSize: "10px",
                          color: "var(--purple)",
                          fontFamily: "var(--mono)",
                          margin: "2px 0",
                        }}
                      >
                        📍 {product.shelf_location}
                      </p>
                    )}
                    <p className="price">ETB {product.selling_price}</p>
                    <p
                      className={`stock ${product.stock_qty <= 0 ? "out" : product.stock_qty <= product.low_stock_threshold ? "low" : ""}`}
                    >
                      {product.stock_qty <= 0
                        ? "Out of stock"
                        : `Stock: ${product.stock_qty}${product.stock_qty <= product.low_stock_threshold ? " ⚠ Low" : ""}`}
                    </p>
                  </button>
                );
              })}
          </div>
        </div>

        {/* Drag divider */}
        <div
          ref={dividerRef}
          className="pos-divider"
          onMouseDown={() => {
            isDragging.current = true;
            document.body.style.cursor = "col-resize";
          }}
        />

        {/* RIGHT — Cart */}
        {showMobileCart && (
          <div
            className="mobile-cart-backdrop open"
            onClick={() => setShowMobileCart(false)}
          />
        )}
        <div
          className={`right ${showMobileCart ? "mobile-expanded" : ""}`}
          style={{ flex: 1, minWidth: 0 }}
        >
          <div className="cart-header">
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <h2>Receipt</h2>
              <span>
                ({cart.length} item{cart.length !== 1 ? "s" : ""})
              </span>
            </div>
            <button
              className="mobile-cart-close"
              onClick={() => setShowMobileCart(false)}
              title="Close and return to products"
            >
              ✕
            </button>
          </div>

          <div className="cart-items">
            {cart.length === 0 ? (
              <div className="cart-empty">
                <span>🛒</span>
                <p>Cart is empty</p>
                <small>Click a product to add it</small>
              </div>
            ) : (
              cart.map((item) => (
                <div key={item.id} className="cart-item">
                  <div className="cart-item-info">
                    <p>{item.name}</p>
                    <small>
                      {item.sku_code && (
                        <span
                          style={{ color: "var(--cyan)", marginRight: "6px" }}
                        >
                          {item.sku_code}
                        </span>
                      )}
                      {item.category_code && (
                        <span
                          style={{ color: "var(--text3)", marginRight: "6px" }}
                        >
                          [{item.category_code}]
                        </span>
                      )}
                      {item.barcode && (
                        <span style={{ color: "var(--text3)" }}>
                          #{item.barcode}
                        </span>
                      )}
                    </small>
                    <small style={{ color: "var(--text3)" }}>
                      ETB {item.selling_price} × {item.qty}
                    </small>
                  </div>

                  <div className="cart-qty-controls">
                    <button
                      className="qty-btn minus"
                      onClick={() => {
                        if (item.qty <= 1) removeFromCart(item.id);
                        else
                          setCart((prev) =>
                            prev.map((i) =>
                              i.id === item.id ? { ...i, qty: i.qty - 1 } : i,
                            ),
                          );
                      }}
                    >
                      −
                    </button>
                    <span className="qty-display">{item.qty}</span>
                    <button
                      className="qty-btn plus"
                      disabled={item.qty >= item.stock_qty}
                      onClick={() =>
                        setCart((prev) =>
                          prev.map((i) =>
                            i.id === item.id && i.qty < i.stock_qty
                              ? { ...i, qty: i.qty + 1 }
                              : i,
                          ),
                        )
                      }
                    >
                      +
                    </button>
                  </div>

                  <span className="cart-item-total">
                    ETB {(item.selling_price * item.qty).toFixed(2)}
                  </span>
                  <button
                    className="cart-item-remove"
                    onClick={() => removeFromCart(item.id)}
                  >
                    ✕
                  </button>
                </div>
              ))
            )}
          </div>

          {/* Checkout */}
          <div className="checkout">
            {vatEnabled && cart.length > 0 && (
              <div
                style={{
                  fontSize: "12px",
                  fontFamily: "var(--mono)",
                  padding: "8px 12px",
                  background: "var(--elevated)",
                  borderRadius: "var(--radius)",
                  marginBottom: "8px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "4px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    color: "var(--text2)",
                  }}
                >
                  <span>Subtotal</span>
                  <span>ETB {cartSubtotal.toFixed(2)}</span>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    color: "var(--amber)",
                  }}
                >
                  <span>VAT ({storeSettings.vat_rate}%)</span>
                  <span>ETB {vatAmount.toFixed(2)}</span>
                </div>
              </div>
            )}

            <div className="total-row">
              <span>TOTAL</span>
              <strong>ETB {totalWithVat.toFixed(2)}</strong>
            </div>

            <div className="payment-methods">
              {[
                { id: "cash", label: "💵 Cash" },
                { id: "telebirr", label: "📱 Telebirr" },
                { id: "bank", label: "🏦 Bank" },
              ].map((m) => (
                <button
                  key={m.id}
                  className={`pay-method-btn ${paymentMethod === m.id ? "active" : ""}`}
                  onClick={() => setPaymentMethod(m.id)}
                >
                  {m.label}
                </button>
              ))}
            </div>

            <button
              className="charge-btn"
              disabled={cart.length === 0}
              onClick={() => {
                setShowMobileCart(false);
                setShowPaymentPopup(true);
              }}
            >
              CHARGE ETB {totalWithVat.toFixed(2)}
            </button>

            {cart.length > 0 && (
              <button className="clear-btn" onClick={() => setCart([])}>
                Clear Cart
              </button>
            )}
          </div>
        </div>

        {/* Mobile-only compact cart bar — tap to open the full Receipt drawer above */}
        <button
          className="mobile-cart-bar"
          onClick={() => setShowMobileCart(true)}
        >
          <span className="mobile-cart-bar-count">
            🧾 {cart.length} item{cart.length !== 1 ? "s" : ""}
          </span>
          <span className="mobile-cart-bar-total">
            ETB {totalWithVat.toFixed(2)}
          </span>
          <span className="mobile-cart-bar-chevron">▲</span>
        </button>
      </div>
    </div>
  );
}
